import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import {
  Phone, MapPin, Clock, Star, Menu, X,
  Layers, Wind, Users, Wine, Utensils,
  ExternalLink, ChevronDown, AlertCircle
} from 'lucide-react';

const PHOTO = {
  hero: 'https://customer-assets.emergentagent.com/job_6fed088e-ba6f-46d7-b7c8-33fd6317d6be/artifacts/8bb0o6af_image.png',
  interior: 'https://customer-assets.emergentagent.com/job_6fed088e-ba6f-46d7-b7c8-33fd6317d6be/artifacts/3vv2mzzz_image.png',
  drinks: 'https://customer-assets.emergentagent.com/job_6fed088e-ba6f-46d7-b7c8-33fd6317d6be/artifacts/wqldc8a0_image.png',
  logo: 'https://customer-assets.emergentagent.com/job_6fed088e-ba6f-46d7-b7c8-33fd6317d6be/artifacts/drunvyq1_image.png',
};

// Scroll-reveal component
const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
};

// ── NAVBAR ───────────────────────────────────────────────────────────────────
const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const links = [
    { label: 'Vibe', id: 'vibe' },
    { label: 'Menu', id: 'menu' },
    { label: 'Gallery', id: 'gallery' },
    { label: 'Visit', id: 'visit' },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-black/92 backdrop-blur-xl border-b border-gold/10 py-3' : 'py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
          >
            <img
              src={PHOTO.logo}
              alt="Somras"
              className="h-10 w-10 object-cover rounded-full ring-1 ring-gold/30 group-hover:ring-gold/70 transition-all duration-300"
            />
            <div className="hidden sm:block">
              <p className="font-playfair font-bold text-base leading-none tracking-wider" style={{ color: '#D4AF37' }}>SOMRAS</p>
              <p className="text-white/35 text-[10px] tracking-[0.22em] uppercase font-manrope mt-0.5">Bar & Kitchen</p>
            </div>
          </button>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {links.map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="relative font-manrope text-white/55 hover:text-gold text-sm uppercase tracking-[0.14em] transition-colors duration-200 group"
              >
                {label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-gold group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="tel:+919663846153"
              data-testid="navbar-reserve-btn"
              className="hidden md:flex items-center gap-2 text-black text-xs font-bold uppercase tracking-wider px-5 py-2.5 transition-all duration-200 hover:shadow-gold-glow"
              style={{ backgroundColor: '#D4AF37' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FFBF00'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4AF37'}
            >
              <Phone size={13} />
              Reserve
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-toggle"
              className="md:hidden text-white/65 hover:text-gold transition-colors p-1"
              aria-label="Toggle navigation menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay menu */}
      <div
        data-testid="mobile-menu"
        aria-hidden={!menuOpen}
      className={`fixed inset-0 z-40 bg-black/96 backdrop-blur-xl flex flex-col items-center justify-center gap-7 transition-all duration-500 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <img src={PHOTO.logo} alt="Somras" className="h-16 w-16 object-cover rounded-full ring-2 ring-gold/30 mb-2" />
        {links.map(({ label, id }) => (
          <button
            key={id}
            onClick={() => scrollTo(id)}
            className="font-playfair text-white/80 hover:text-gold text-4xl transition-colors duration-200"
          >
            {label}
          </button>
        ))}
        <a
          href="tel:+919663846153"
          data-testid="mobile-reserve-btn"
          onClick={() => setMenuOpen(false)}
          className="mt-4 flex items-center gap-2 bg-gold text-black font-bold uppercase tracking-wider px-8 py-3.5 text-sm hover:bg-gold-bright transition-colors duration-200"
        >
          <Phone size={15} />
          Reserve a Table
        </a>
      </div>
    </>
  );
};

// ── HERO ─────────────────────────────────────────────────────────────────────
const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={PHOTO.hero} alt="Somras Bar interior" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/45 to-black/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-black/55" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_55%,rgba(212,175,55,0.10)_0%,transparent_60%)]" />
    </div>

    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
      <div className="inline-flex items-center gap-3 text-gold/65 text-[11px] tracking-[0.32em] uppercase font-manrope mb-8 fade-in-up" style={{ animationDelay: '0.2s' }}>
        <span className="w-10 h-px bg-gold/40" />
        HSR Layout, Bengaluru
        <span className="w-10 h-px bg-gold/40" />
      </div>

      <h1 className="font-playfair text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight text-white mb-6 fade-in-up" style={{ animationDelay: '0.4s' }}>
        Nights in HSR,{' '}
        <span className="gold-shimmer block sm:inline">shaken to perfection.</span>
      </h1>

      <p className="font-manrope text-white/60 text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed fade-in-up" style={{ animationDelay: '0.6s' }}>
        Open daily 12 PM – 1 AM &nbsp;·&nbsp; Craft cocktails by our bar team &nbsp;·&nbsp; DJ nights, two floors, zero dull moments.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center fade-in-up" style={{ animationDelay: '0.8s' }}>
        <a
          href="tel:+919663846153"
          data-testid="hero-reserve-btn"
          className="group flex items-center gap-2.5 text-black font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 hover:shadow-gold-glow hover:scale-[1.02] w-full sm:w-auto justify-center"
          style={{ backgroundColor: '#D4AF37' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FFBF00'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4AF37'}
        >
          <Phone size={15} />
          Reserve a Table
        </a>
        <a
          href="https://maps.google.com/?cid=6276156542877102647"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-directions-btn"
          className="flex items-center gap-2.5 border border-gold/50 hover:border-gold text-gold hover:bg-gold/10 font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 w-full sm:w-auto justify-center"
        >
          <MapPin size={15} />
          Get Directions
        </a>
      </div>

      <div className="mt-10 flex items-center justify-center gap-2.5 text-white/35 text-sm font-manrope fade-in-up" style={{ animationDelay: '1s' }}>
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="#D4AF37" color="#D4AF37" />)}
        </div>
        <span>4.8 &nbsp;·&nbsp; 5,097 Google reviews</span>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/25 animate-bounce fade-in-up" style={{ animationDelay: '1.2s' }}>
      <ChevronDown size={22} />
    </div>
  </section>
);

// ── WHAT TO EXPECT ────────────────────────────────────────────────────────────
const FEATURES = [
  { Icon: Layers, title: 'Two Spacious Floors', desc: "Different energy on each level — find your zone, whether it's intimate booth dining or lively bar crowd." },
  { Icon: Wind, title: 'Open-Air Seating', desc: 'Step into the breeze on our open-air section. Perfect for late evenings with a cool drink in hand.' },
  { Icon: Wine, title: 'Craft Cocktail Bar', desc: 'Our bar team crafts every pour with intention — from tried classics to our original signatures.' },
  { Icon: Users, title: 'Attentive Service', desc: "Staff that reads the room — always around when you need them, never in the way when you don't." },
];

const WhatToExpect = () => (
  <section id="vibe" data-testid="vibe-section" className="relative py-24 md:py-32 bg-charcoal-50 overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-gold/5 rounded-full blur-[130px] pointer-events-none" aria-hidden="true" />

    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="text-center mb-16">
        <p className="text-gold/55 text-[11px] tracking-[0.35em] uppercase font-manrope mb-3">The Experience</p>
        <h2 className="font-playfair text-4xl md:text-5xl text-white font-bold">What to Expect</h2>
        <div className="mt-5 w-16 h-0.5 bg-gold mx-auto" />
      </FadeIn>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
        {FEATURES.map(({ Icon, title, desc }, i) => (
          <FadeIn key={title} delay={i * 100}>
            <div
              data-testid={`feature-card-${i}`}
              className="group p-7 border border-white/[0.06] hover:border-gold/25 bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-300 h-full flex flex-col"
            >
              <div className="w-11 h-11 rounded-full border border-gold/25 flex items-center justify-center mb-5 group-hover:border-gold/60 group-hover:shadow-gold-glow transition-all duration-300 shrink-0">
                <Icon color="#D4AF37" size={18} />
              </div>
              <h3 className="font-playfair text-white text-lg font-semibold mb-3">{title}</h3>
              <p className="font-manrope text-white/45 text-sm leading-relaxed">{desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={400}>
        <div className="relative overflow-hidden h-64 md:h-[420px]">
          <img src={PHOTO.interior} alt="Somras Bar & Kitchen — Two Floors" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/25 to-black/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 md:left-12 right-8">
            <p className="font-playfair text-white text-2xl md:text-4xl font-bold leading-tight">
              Two floors,<br />
              <span className="gold-shimmer">one unforgettable night.</span>
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

// ── MENU HIGHLIGHTS ───────────────────────────────────────────────────────────
const MENU_ITEMS = [
  { category: 'Signature Cocktail', name: 'Tequila Sunset', desc: 'Layered premium tequila, fresh-squeezed citrus, and house grenadine — warm hues, bold finish every time.', icon: Wine, badge: 'Fan Favourite' },
  { category: 'From the Sea', name: 'Basha Fish', desc: 'Fresh pomfret with coastal spice rub, charred to perfection and served with our house chutney.', icon: Utensils, badge: 'Must Try' },
  { category: 'Starters', name: 'Jalapeño Cheese Poppers', desc: 'Fiery jalapeños loaded with molten cheese, flash-fried to golden crunch. The perfect night starter.', icon: Utensils, badge: '' },
  { category: 'Starters', name: 'Chicken Wings', desc: 'Smoky, sticky glazed wings with our signature house dry rub. Addictively good — order double.', icon: Utensils, badge: 'Most Ordered' },
  { category: 'Mains', name: 'Hakka Noodles', desc: 'Wok-tossed noodles with crisp vegetables, a hint of soy and sesame oil. A crowd-pleaser, always.', icon: Utensils, badge: '' },
];

const MenuHighlights = () => (
  <section id="menu" data-testid="menu-section" className="relative py-24 md:py-32 bg-charcoal overflow-hidden">
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <img src={PHOTO.drinks} alt="" className="w-full h-full object-cover opacity-[0.04]" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="text-center mb-16">
        <p className="text-gold/55 text-[11px] tracking-[0.35em] uppercase font-manrope mb-3">The Spread</p>
        <h2 className="font-playfair text-4xl md:text-5xl text-white font-bold">What's Good Tonight</h2>
        <div className="mt-5 w-16 h-0.5 bg-gold mx-auto" />
        <p className="mt-6 font-manrope text-white/40 text-sm max-w-md mx-auto leading-relaxed">
          Ordered by regulars, remembered for weeks. A curated selection of our most-loved plates and pours.
        </p>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.04]">
        {MENU_ITEMS.map((item, i) => (
          <FadeIn key={item.name} delay={i * 80}>
            <div
              data-testid={`menu-item-${i}`}
              className="relative group bg-charcoal hover:bg-[#0f0f0f] transition-colors duration-300 p-8 h-full flex flex-col"
            >
              {item.badge && (
                <span className="absolute top-5 right-5 text-[9px] font-manrope font-extrabold text-black bg-gold px-2.5 py-0.5 uppercase tracking-widest">
                  {item.badge}
                </span>
              )}
              <p className="text-gold/45 text-[10px] tracking-[0.25em] uppercase font-manrope mb-3">{item.category}</p>
              <h3 className="font-playfair text-white text-xl font-bold mb-3 group-hover:text-gold transition-colors duration-300">
                {item.name}
              </h3>
              <p className="font-manrope text-white/45 text-sm leading-relaxed flex-1">{item.desc}</p>
              <div className="mt-5 w-0 group-hover:w-8 h-px bg-gold transition-all duration-500" />
            </div>
          </FadeIn>
        ))}
      </div>

      <FadeIn delay={500}>
        <div className="relative overflow-hidden h-44 md:h-60 mt-px">
          <img src={PHOTO.drinks} alt="Craft drinks at Somras" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/85" />
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <div>
              <p className="font-playfair text-white text-xl md:text-2xl font-semibold italic leading-snug">
                "Tequila Sunset was neatly made<br className="hidden md:block" /> and well balanced."
              </p>
              <p className="font-manrope text-gold/70 text-sm mt-3">— Sachin, Google Review</p>
            </div>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

// ── GALLERY ───────────────────────────────────────────────────────────────────
const GALLERY = [
  { src: PHOTO.hero,     caption: 'The bar that never sleeps',  sub: 'Glowing shelves, golden nights' },
  { src: PHOTO.interior, caption: 'Two floors, endless stories', sub: 'Every table, a new chapter' },
  { src: PHOTO.drinks,   caption: 'Crafted, not poured',        sub: 'Every drink tells a story' },
];

const Gallery = () => (
  <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-charcoal-50">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="text-center mb-16">
        <p className="text-gold/55 text-[11px] tracking-[0.35em] uppercase font-manrope mb-3">Inside Somras</p>
        <h2 className="font-playfair text-4xl md:text-5xl text-white font-bold">The Vibe in Frames</h2>
        <div className="mt-5 w-16 h-0.5 bg-gold mx-auto" />
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {GALLERY.map((item, i) => (
          <FadeIn key={item.caption} delay={i * 120}>
            <div
              data-testid={`gallery-photo-${i}`}
              className="group relative overflow-hidden aspect-[4/5] cursor-pointer"
            >
              <img
                src={item.src}
                alt={item.caption}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-playfair text-white text-lg font-semibold leading-tight">{item.caption}</h3>
                <p className="font-manrope text-white/50 text-xs mt-1.5 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {item.sub}
                </p>
                <div className="mt-3 w-0 group-hover:w-8 h-0.5 bg-gold transition-all duration-500" />
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

// ── TESTIMONIALS ──────────────────────────────────────────────────────────────
const REVIEWS = [
  { name: 'Pavan',       review: 'Great place to relax with friends. Good food, drinks, nice ambiance. Staff were polite and attentive.', stars: 5 },
  { name: 'Char Charan', review: 'Nice dim-light ambience with open-air seating, mocktails and juices were refreshing.', stars: 5 },
  { name: 'Vivek',       review: 'Layout is generous, spanning two floors… perfect for casual hangouts.', stars: 5 },
  { name: 'Sachin',      review: 'Tequila Sunset was neatly made and well balanced… pomfret fresh and cooked to perfection.', stars: 5 },
];

const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-24 md:py-32 bg-charcoal overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="text-center mb-16">
        <p className="text-gold/55 text-[11px] tracking-[0.35em] uppercase font-manrope mb-3">Word on the Street</p>
        <h2 className="font-playfair text-4xl md:text-5xl text-white font-bold">What Our Guests Say</h2>
        <div className="mt-5 w-16 h-0.5 bg-gold mx-auto" />
        <div className="mt-5 flex items-center justify-center gap-2">
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#D4AF37" color="#D4AF37" />)}
          </div>
          <span className="font-manrope text-white/40 text-sm">4.8 average &nbsp;·&nbsp; 5,097 reviews on Google</span>
        </div>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {REVIEWS.map(({ name, review, stars }, i) => (
          <FadeIn key={name} delay={i * 100}>
            <div
              data-testid={`review-card-${i}`}
              className="group p-8 border border-white/[0.06] hover:border-gold/20 bg-white/[0.015] hover:bg-white/[0.03] transition-all duration-300 flex flex-col gap-5 h-full"
            >
              <div className="flex gap-1">
                {[...Array(stars)].map((_, j) => <Star key={j} size={13} fill="#D4AF37" color="#D4AF37" />)}
              </div>
              <blockquote className="font-manrope text-white/75 text-base leading-relaxed italic flex-1">
                "{review}"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/25 flex items-center justify-center shrink-0">
                  <span className="font-playfair text-gold font-bold text-sm">{name[0]}</span>
                </div>
                <div>
                  <p className="font-manrope text-white/85 text-sm font-semibold">{name}</p>
                  <p className="font-manrope text-white/30 text-xs">Google Review</p>
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

// ── PLAN YOUR NIGHT ───────────────────────────────────────────────────────────
const PlanYourNight = () => (
  <section id="visit" data-testid="visit-section" className="py-24 md:py-32 bg-charcoal-50">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="text-center mb-16">
        <p className="text-gold/55 text-[11px] tracking-[0.35em] uppercase font-manrope mb-3">We're Waiting</p>
        <h2 className="font-playfair text-4xl md:text-5xl text-white font-bold">Plan Your Night</h2>
        <div className="mt-5 w-16 h-0.5 bg-gold mx-auto" />
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Info column */}
        <FadeIn>
          <div className="space-y-8">
            <div className="flex gap-5">
              <div className="w-12 h-12 shrink-0 rounded-full border border-gold/25 flex items-center justify-center">
                <Clock color="#D4AF37" size={18} />
              </div>
              <div>
                <p className="font-playfair text-white text-lg font-semibold mb-1">Hours</p>
                <p className="font-manrope text-white/55 text-sm">Open Daily &nbsp;·&nbsp; 12:00 PM – 1:00 AM</p>
                <p className="font-manrope text-gold/60 text-xs mt-1">Open 365 days a year. No excuses.</p>
              </div>
            </div>

            <div className="flex gap-5">
              <div className="w-12 h-12 shrink-0 rounded-full border border-gold/25 flex items-center justify-center">
                <Phone color="#D4AF37" size={18} />
              </div>
              <div>
                <p className="font-playfair text-white text-lg font-semibold mb-1">Reservations</p>
                <a
                  href="tel:+919663846153"
                  data-testid="visit-phone-link"
                  className="font-manrope text-white/55 text-sm hover:text-gold transition-colors duration-200"
                >
                  +91 96638 46153
                </a>
                <p className="font-manrope text-white/30 text-xs mt-1">Call us, especially for weekend nights.</p>
              </div>
            </div>

            <div className="flex gap-5">
              <div className="w-12 h-12 shrink-0 rounded-full border border-gold/25 flex items-center justify-center">
                <MapPin color="#D4AF37" size={18} />
              </div>
              <div>
                <p className="font-playfair text-white text-lg font-semibold mb-1">Address</p>
                <p className="font-manrope text-white/55 text-sm leading-relaxed">
                  2331, 17th Cross Rd, 1st Sector,<br />
                  HSR Layout, Bengaluru 560102
                </p>
                <a
                  href="https://maps.google.com/?cid=6276156542877102647"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="visit-maps-link"
                  className="mt-2 inline-flex items-center gap-1.5 text-gold/65 text-xs font-manrope hover:text-gold transition-colors"
                >
                  Open in Google Maps <ExternalLink size={11} />
                </a>
              </div>
            </div>

            {/* Parking warning */}
            <div className="flex gap-4 p-5 border border-gold-bright/15 bg-gold-bright/[0.03]">
              <AlertCircle color="#FFBF00" className="shrink-0 mt-0.5" size={17} />
              <div>
                <p className="font-manrope text-white/75 text-sm font-semibold mb-1">Heads-up on Parking</p>
                <p className="font-manrope text-white/40 text-xs leading-relaxed">
                  Street parking can be tight on busy nights. We recommend arriving early or taking a cab or auto.
                </p>
              </div>
            </div>

            <a
              href="tel:+919663846153"
              data-testid="visit-reserve-btn"
              className="inline-flex items-center gap-2.5 bg-gold hover:bg-gold-bright text-black font-bold uppercase tracking-wider px-8 py-4 text-sm transition-all duration-300 hover:shadow-gold-glow"
            >
              <Phone size={15} />
              Reserve a Table
            </a>
          </div>
        </FadeIn>

        {/* Map column */}
        <FadeIn delay={200}>
          <div
            data-testid="google-map"
            className="relative overflow-hidden border border-white/[0.06] h-[400px] lg:h-[520px]"
          >
            <iframe
              src="https://maps.google.com/maps?q=2331+17th+Cross+Rd+1st+Sector+HSR+Layout+Bengaluru+560102&hl=en&z=16&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) saturate(0.6)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SOMRAS BAR & KITCHEN Location"
            />
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

// ── FOOTER ────────────────────────────────────────────────────────────────────
const Footer = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <footer data-testid="footer" className="bg-black border-t border-white/[0.05] py-14">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* CTA banner */}
        <div className="relative mb-12 py-12 flex flex-col items-center gap-4 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.10)_0%,transparent_70%)] border border-gold/10">
          <img src={PHOTO.logo} alt="Somras" className="h-20 w-20 object-contain" />
          <p className="font-playfair text-gold text-2xl font-bold tracking-wider">SOMRAS BAR & KITCHEN</p>
          <p className="font-manrope text-white/35 text-xs tracking-[0.2em] uppercase text-center px-4">
            Your crew's go-to night-out spot in HSR Layout
          </p>
          <a
            href="tel:+919663846153"
            data-testid="footer-reserve-btn"
            className="mt-2 flex items-center gap-2 bg-gold hover:bg-gold-bright text-black font-bold uppercase tracking-wider px-6 py-3 text-xs transition-all duration-300 hover:shadow-gold-glow"
          >
            <Phone size={13} />
            Reserve Now
          </a>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.05] pt-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <p className="font-manrope text-white/25 text-xs text-center md:text-left">
            2331, 17th Cross Rd · HSR Layout · Bengaluru 560102
          </p>
          <div className="flex gap-6 text-[11px] font-manrope uppercase tracking-widest text-white/25">
            {['vibe', 'menu', 'gallery', 'visit'].map(id => (
              <button key={id} onClick={() => go(id)} className="hover:text-gold transition-colors capitalize">
                {id}
              </button>
            ))}
          </div>
          <p className="font-manrope text-white/20 text-xs">© 2025 Somras Bar & Kitchen</p>
        </div>
      </div>
    </footer>
  );
};

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <div data-testid="app" style={{ background: '#0A0A0A' }}>
      <Navbar />
      <Hero />
      <WhatToExpect />
      <MenuHighlights />
      <Gallery />
      <Testimonials />
      <PlanYourNight />
      <Footer />
    </div>
  );
}
